/**
 * submitCortexBackgroundJob — shared cortex background job enqueue logic.
 */

import type { SupabaseClient } from "@supabase/supabase-js";
import { buildPGMQMessage } from "@flowstate/mycelium-core";

export interface SubmitCortexBackgroundJobResult {
  taskId?: string;
  dedupKey: string;
  error?: string;
}

export interface SubmitCortexBackgroundJobParams {
  supabase: SupabaseClient;
  userId: string;
  orgId: string | null;
  prompt: string;
  specialist?: string;
  dedupKey?: string;
  source?: string;
  retryOf?: string;
}

export function buildCortexBackgroundQueueMessage({
  userId,
  orgId,
  prompt,
  specialist,
  dedupKey,
  source = "cortex-background-job",
  retryOf,
}: Omit<SubmitCortexBackgroundJobParams, "supabase"> & { dedupKey: string }) {
  return buildPGMQMessage({
    domain: "ai",
    type: "cortex_context_enriched",
    priority: "normal",
    source,
    userId,
    principalId: userId,
    orgId: orgId ?? undefined,
    deduplicationKey: dedupKey,
    payload: {
      messages: [{ role: "user", content: prompt }],
      cortexContext: {
        backgroundJob: true,
        specialist: specialist ?? undefined,
        ...(retryOf ? { retryOf } : {}),
      },
      sessionId: `bg-${dedupKey}`,
    },
  });
}

export async function submitCortexBackgroundJob({
  supabase,
  userId,
  orgId,
  prompt,
  specialist,
  dedupKey: dedupKeyInput,
  source,
  retryOf,
}: SubmitCortexBackgroundJobParams): Promise<SubmitCortexBackgroundJobResult> {
  const dedupKey = dedupKeyInput ?? `cortex-job:${userId}:${Date.now()}`;

  const queueMessage = buildCortexBackgroundQueueMessage({
    userId,
    orgId,
    prompt,
    specialist,
    dedupKey,
    source,
    retryOf,
  });

  const { error: queueError } = await supabase.rpc("pgmq_send", {
    p_queue_name: "cortex_tasks",
    p_message: queueMessage,
  });

  if (queueError) {
    return { dedupKey, error: `Queue error: ${queueError.message}` };
  }

  try {
    const { data: logData } = await supabase
      .from("cortex_task_log")
      .insert({
        trigger_event_domain: queueMessage.domain,
        trigger_event_type: "cortex_context_enriched",
        user_id: userId,
        org_id: orgId ?? null,
        intent: specialist ? `background_job (${specialist})` : "background_job",
        context: {
          prompt,
          specialist: specialist ?? null,
          backgroundJob: true,
          submittedAt: new Date().toISOString(),
          ...(retryOf ? { retryOf } : {}),
        },
        status: "queued",
        dedup_key: dedupKey,
      })
      .select("id")
      .single();

    return { taskId: logData?.id, dedupKey };
  } catch {
    return { dedupKey };
  }
}
